import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  useBreakpointValue,
  IconButton,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Document, Page, Thumbnail, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configure PDF.js worker (must be in same module as Document/Page usage)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Source for the PDF: File (e.g. from input), URL string, or object for react-pdf. Reusable for preview and for rendering result. */
export type DocViewerFile = File | string | { url: string };

/** Result of document processing: extracted and/or translated text to show in the same viewer layout. */
export type DocViewerResult = {
  extractedText: string | null;
  translatedText: string | null;
  translatedLanguageName?: string;
  translatedTruncated?: boolean;
};

export type DocViewerProps = {
  /** PDF source (for preview or PDF result). */
  file?: DocViewerFile;
  /** Processed result (extracted + translation). When set, same UI as preview but shows markdown panels. */
  result?: DocViewerResult;
  /** Prefix for element ids (e.g. "process-document-pdf" or "doc-result"). */
  idPrefix?: string;
  /** Max height of the viewer area in pixels. */
  maxHeight?: number;
  /** Hide thumbnail rail and toggle in both PDF and result views. */
  hideThumbnails?: boolean;
  /** Controlled mobile thumbnail expansion state. */
  mobileThumbnailsExpanded?: boolean;
  /** Callback for controlled mobile thumbnail expansion updates. */
  onMobileThumbnailsExpandedChange?: (expanded: boolean) => void;
  /** Hide the inline mobile thumbnail toggle button. */
  hideInlineThumbnailToggle?: boolean;
};

const markdownSx = {
  "& p": { mb: 2 },
  "& ul, & ol": { pl: 4, mb: 2 },
  "& h1, & h2, & h3": { fontSize: "sm", fontWeight: 600, mt: 2, mb: 1 },
  "& table": {
    width: "100%",
    borderCollapse: "collapse" as const,
    mb: 2,
    fontSize: "sm",
  },
  "& th, & td": {
    border: "1px solid",
    borderColor: "gray.200",
    px: 2,
    py: 1,
    textAlign: "left" as const,
  },
  "& th": { fontWeight: 600 },
  "& tr:nth-of-type(even)": { bg: "gray.50" },
};

/** Heuristic: content looks like HTML (e.g. from /convert with output_type=html). */
function looksLikeHtml(content: string): boolean {
  const trimmed = content.trim();
  return trimmed.length > 0 && trimmed.startsWith("<") && trimmed.includes(">");
}

function splitPymupdfPages(content: string): string[] {
  if (typeof window === "undefined") return [content];
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${content}</body>`, "text/html");
  const pages = Array.from(doc.querySelectorAll('div[id^="page"]'));
  if (pages.length === 0) return [content];
  return pages.map((page) => page.outerHTML);
}

function buildIframeSrcDoc(content: string): string {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background: transparent;
      }
      body {
        padding: 0;
      }
      div[id^="page"] {
        position: relative !important;
        margin: 0 auto 12px auto;
        background: #fff;
        overflow: hidden;
      }
      div[id^="page"]:last-child {
        margin-bottom: 0;
      }
      /* PyMuPDF html relies on absolute paragraph positioning. */
      div[id^="page"] p {
        position: absolute;
        margin: 0;
        white-space: pre;
      }
      div[id^="page"] span {
        white-space: pre;
      }
      div[id^="page"] img {
        position: absolute;
      }
    </style>
  </head>
  <body>${content}</body>
</html>`;
}

function getPymupdfPageSizePx(pageHtml: string): {
  width: number;
  height: number;
} {
  const DEFAULT_WIDTH_PT = 612;
  const DEFAULT_HEIGHT_PT = 792;

  const toPx = (value: number, unit: string): number =>
    unit.toLowerCase() === "pt" ? value * (96 / 72) : value;

  const parseSizeFromStyle = (
    styleValue: string,
    property: "width" | "height",
  ): number | null => {
    const match = styleValue.match(
      new RegExp(`${property}\\s*:\\s*([\\d.]+)\\s*(pt|px)`, "i"),
    );
    if (!match) return null;
    const value = Number.parseFloat(match[1]);
    if (!Number.isFinite(value) || value <= 0) return null;
    return toPx(value, match[2]);
  };

  let widthPx: number | null = null;
  let heightPx: number | null = null;

  if (typeof window !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(pageHtml, "text/html");
    const pageEl =
      doc.querySelector('div[id^="page"]') ??
      (doc.body.firstElementChild as HTMLElement | null);
    const styleValue = pageEl?.getAttribute("style") ?? "";
    widthPx = parseSizeFromStyle(styleValue, "width");
    heightPx = parseSizeFromStyle(styleValue, "height");
  }

  const width = Math.max(1, widthPx ?? DEFAULT_WIDTH_PT * (96 / 72));
  const height = Math.max(1, heightPx ?? DEFAULT_HEIGHT_PT * (96 / 72));
  const aspectRatio = height / width;

  // Guard against malformed/partial page style parsing that causes squished thumbnails.
  if (!Number.isFinite(aspectRatio) || aspectRatio < 0.8 || aspectRatio > 2.2) {
    return {
      width: DEFAULT_WIDTH_PT * (96 / 72),
      height: DEFAULT_HEIGHT_PT * (96 / 72),
    };
  }

  return { width, height };
}

type ResultPanel = "extracted" | "translation";

const DocViewer = ({
  file,
  result,
  idPrefix = "doc-viewer",
  maxHeight = 420,
  hideThumbnails = false,
  mobileThumbnailsExpanded,
  onMobileThumbnailsExpandedChange,
  hideInlineThumbnailToggle = false,
}: DocViewerProps) => {
  const { t } = useTranslation();
  const cardBorder = useColorModeValue("gray.200", "gray.600");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [thumbnailExpandedInternal, setThumbnailExpandedInternal] =
    useState(false);
  const [pageContainerWidth, setPageContainerWidth] = useState<number>(400);
  const [pdfNaturalWidth, setPdfNaturalWidth] = useState<number | null>(null);
  const [resultPanel, setResultPanel] = useState<ResultPanel>("extracted");
  const [htmlPageNumber, setHtmlPageNumber] = useState(1);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const pageRefsRef = useRef<(HTMLDivElement | null)[]>([]);
  const htmlPageRefsRef = useRef<(HTMLDivElement | null)[]>([]);

  const isLargeScreen = useBreakpointValue({ base: false, md: true }) ?? false;
  const isThumbnailControlled = mobileThumbnailsExpanded !== undefined;
  const thumbnailExpanded =
    mobileThumbnailsExpanded ?? thumbnailExpandedInternal;
  const setThumbnailExpanded = (
    next: boolean | ((prev: boolean) => boolean),
  ) => {
    const nextValue =
      typeof next === "function" ? next(thumbnailExpanded) : next;
    if (mobileThumbnailsExpanded === undefined) {
      setThumbnailExpandedInternal(nextValue);
    }
    onMobileThumbnailsExpandedChange?.(nextValue);
  };
  const showThumbnails =
    !hideThumbnails &&
    (isThumbnailControlled
      ? thumbnailExpanded
      : isLargeScreen || thumbnailExpanded);

  const isResultMode = result != null;
  const hasExtracted = isResultMode && result.extractedText != null;
  const hasTranslation = isResultMode && result.translatedText != null;

  useEffect(() => {
    if (isResultMode)
      setResultPanel(hasExtracted ? "extracted" : "translation");
  }, [isResultMode, hasExtracted]);

  useEffect(() => {
    const el = pageContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0]?.contentRect ?? {};
      if (typeof width === "number" && width > 0) setPageContainerWidth(width);
    });
    ro.observe(el);
    setPageContainerWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [file, result, showThumbnails, numPages]);

  const resultContentForView = isResultMode
    ? resultPanel === "extracted"
      ? (result?.extractedText ?? "")
      : (result?.translatedText ?? "")
    : "";
  const isResultHtmlContent =
    isResultMode && looksLikeHtml(resultContentForView);
  const resultHtmlPages = isResultHtmlContent
    ? splitPymupdfPages(resultContentForView)
    : [];
  const displayResultHtmlPages =
    resultHtmlPages.length > 0 ? resultHtmlPages : [resultContentForView];

  useEffect(() => {
    if (isResultMode && isResultHtmlContent) {
      setHtmlPageNumber(1);
    }
  }, [resultContentForView, isResultMode, isResultHtmlContent]);

  useEffect(() => {
    setPdfNaturalWidth(null);
  }, [file]);

  // Result mode: same layout (sidebar + main when multiple panels, full-width when single)
  if (isResultMode) {
    const panels: { id: ResultPanel; label: string }[] = [];
    if (hasExtracted)
      panels.push({
        id: "extracted",
        label: t("document.extractedText", "Extracted text"),
      });
    if (hasTranslation)
      panels.push({
        id: "translation",
        label: t("document.translation", "Translation"),
      });
    const currentContent = resultContentForView;
    const isHtmlContent = isResultHtmlContent;
    const displayHtmlPages = displayResultHtmlPages;
    const availableWidth = Math.max(1, pageContainerWidth);
    const singlePanel = panels.length <= 1;

    return (
      <Box id={`${idPrefix}-wrapper`} p={2} w="100%">
        <Box
          id={idPrefix}
          display="flex"
          flexDirection="row"
          gap={3}
          maxH={`${maxHeight}px`}
          minH={0}
          w="100%"
          overflow="hidden"
          alignItems="stretch"
        >
          {isHtmlContent && showThumbnails && displayHtmlPages.length > 0 && (
            <Box
              id={`${idPrefix}-thumbnails`}
              flexShrink={0}
              w="100px"
              minW="100px"
              maxH={`${maxHeight}px`}
              overflowY="auto"
              overflowX="hidden"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
              py={1}
              borderRightWidth="1px"
              borderColor={cardBorder}
              pr={2}
            >
              {displayHtmlPages.map((pageHtml, i) => {
                const thumbSize = getPymupdfPageSizePx(pageHtml);
                const thumbDisplayWidth = 72;
                const thumbScale = thumbDisplayWidth / thumbSize.width;
                const thumbW = thumbDisplayWidth;
                const thumbH = Math.round(thumbSize.height * thumbScale);
                return (
                  <Box
                    key={i}
                    id={`${idPrefix}-thumbnail-html-${i + 1}`}
                    flexShrink={0}
                    minW={`${thumbW}px`}
                    minH={`${thumbH}px`}
                    onClick={() => {
                      setHtmlPageNumber(i + 1);
                      htmlPageRefsRef.current[i]?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                    cursor="pointer"
                    borderWidth="2px"
                    borderColor={
                      htmlPageNumber === i + 1 ? "blue.500" : "transparent"
                    }
                    borderRadius="md"
                    _hover={{ borderColor: "gray.400" }}
                    overflow="hidden"
                    bg="white"
                  >
                    <Box
                      width={`${thumbW}px`}
                      height={`${thumbH}px`}
                      minWidth={`${thumbW}px`}
                      minHeight={`${thumbH}px`}
                      overflow="hidden"
                      position="relative"
                    >
                      <iframe
                        title={`${idPrefix}-thumbnail-frame-${i + 1}`}
                        srcDoc={buildIframeSrcDoc(pageHtml)}
                        sandbox=""
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: `${thumbSize.width}px`,
                          height: `${thumbSize.height}px`,
                          border: "none",
                          transform: `scale(${thumbScale})`,
                          transformOrigin: "top left",
                          pointerEvents: "none",
                          background: "white",
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
          {!isHtmlContent &&
            !singlePanel &&
            showThumbnails &&
            panels.length > 0 && (
              <Box
                id={`${idPrefix}-thumbnails`}
                flexShrink={0}
                w="100px"
                minW="100px"
                maxH={`${maxHeight}px`}
                overflowY="auto"
                overflowX="hidden"
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
                py={1}
                borderRightWidth="1px"
                borderColor={cardBorder}
                pr={2}
              >
                {panels.map(({ id, label }) => (
                  <Box
                    key={id}
                    id={`${idPrefix}-thumbnail-${id}`}
                    onClick={() => setResultPanel(id)}
                    cursor="pointer"
                    borderWidth="2px"
                    borderColor={
                      resultPanel === id ? "blue.500" : "transparent"
                    }
                    borderRadius="md"
                    _hover={{ borderColor: "gray.400" }}
                    p={2}
                    textAlign="center"
                  >
                    <Text fontSize="xs" noOfLines={3}>
                      {label}
                    </Text>
                  </Box>
                ))}
              </Box>
            )}
          {isHtmlContent &&
            !hideThumbnails &&
            !isLargeScreen &&
            !hideInlineThumbnailToggle &&
            displayHtmlPages.length > 0 && (
              <Box
                id={`${idPrefix}-thumbnail-toggle-wrap`}
                flexShrink={0}
                alignSelf="flex-start"
                pt={1}
              >
                <IconButton
                  id={`${idPrefix}-thumbnail-toggle`}
                  aria-label={
                    showThumbnails
                      ? t("document.hidePages", "Hide pages")
                      : t("document.showPages", "Show pages")
                  }
                  icon={
                    showThumbnails ? <ChevronLeftIcon /> : <ChevronRightIcon />
                  }
                  size="sm"
                  variant="outline"
                  onClick={() => setThumbnailExpanded((v) => !v)}
                />
              </Box>
            )}
          {!isHtmlContent &&
            !singlePanel &&
            !hideThumbnails &&
            !isLargeScreen &&
            !hideInlineThumbnailToggle &&
            panels.length > 1 && (
              <Box
                id={`${idPrefix}-thumbnail-toggle-wrap`}
                flexShrink={0}
                alignSelf="flex-start"
                pt={1}
              >
                <IconButton
                  id={`${idPrefix}-thumbnail-toggle`}
                  aria-label={
                    showThumbnails
                      ? t("document.hidePages", "Hide pages")
                      : t("document.showPages", "Show pages")
                  }
                  icon={
                    showThumbnails ? <ChevronLeftIcon /> : <ChevronRightIcon />
                  }
                  size="sm"
                  variant="outline"
                  onClick={() => setThumbnailExpanded((v) => !v)}
                />
              </Box>
            )}
          <Box
            id={`${idPrefix}-main`}
            ref={pageContainerRef}
            flex="1"
            minW={0}
            minWidth="200px"
            overflowY="auto"
            overflowX="hidden"
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            position="relative"
            p={isHtmlContent ? 0 : 4}
          >
            {resultPanel === "translation" && result.translatedTruncated && (
              <Box mb={2}>
                <Text fontSize="xs" color={mutedColor}>
                  {t("document.firstNChars", "first 3000 characters only")}
                </Text>
              </Box>
            )}
            <Box
              id={`${idPrefix}-result-content`}
              fontSize={isHtmlContent ? undefined : "sm"}
              lineHeight={isHtmlContent ? undefined : "tall"}
              sx={isHtmlContent ? undefined : markdownSx}
              className={isHtmlContent ? "converted-html" : undefined}
            >
              {isHtmlContent ? (
                <>
                  {displayHtmlPages.map((pageHtml, i) => {
                    const pageSize = getPymupdfPageSizePx(pageHtml);
                    const scale = Math.min(1, availableWidth / pageSize.width);
                    return (
                      <Box
                        key={i}
                        ref={(el) => {
                          htmlPageRefsRef.current[i] = el;
                        }}
                        mb={i < displayHtmlPages.length - 1 ? 3 : 0}
                        display="flex"
                        justifyContent="center"
                        width="100%"
                      >
                        <Box
                          width={`${pageSize.width * scale}px`}
                          height={`${pageSize.height * scale}px`}
                          overflow="hidden"
                          bg="white"
                        >
                          <iframe
                            title={`${idPrefix}-html-page-${i + 1}`}
                            srcDoc={buildIframeSrcDoc(pageHtml)}
                            sandbox=""
                            style={{
                              width: `${pageSize.width}px`,
                              height: `${pageSize.height}px`,
                              border: "none",
                              background: "white",
                              transform: `scale(${scale})`,
                              transformOrigin: "top left",
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm as any]}>
                  {currentContent || "—"}
                </ReactMarkdown>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // PDF mode (file required)
  if (file == null) return null;

  const pdfRenderWidth = Math.max(
    200,
    Math.min(pageContainerWidth, pdfNaturalWidth ?? pageContainerWidth),
  );

  return (
    <Box
      id={`${idPrefix}-wrapper`}
      p={2}
      w="100%"
      sx={{
        [`& #${idPrefix}`]: {
          width: "100%",
          maxHeight: `${maxHeight}px`,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Document
        file={file}
        className={`${idPrefix}-doc`}
        inputRef={(el) => {
          if (el) el.id = idPrefix;
        }}
        onLoadSuccess={(pdf) => {
          setNumPages(pdf.numPages);
          pdf
            .getPage(1)
            .then((firstPage) => {
              const viewport = firstPage.getViewport({ scale: 1 });
              if (Number.isFinite(viewport.width) && viewport.width > 0) {
                setPdfNaturalWidth(viewport.width);
              }
            })
            .catch(() => {
              // Keep fallback behavior when first page metrics cannot be read.
              setPdfNaturalWidth(null);
            });
        }}
        loading={
          <Text
            id={`${idPrefix}-loading`}
            py={4}
            color={mutedColor}
            fontSize="sm"
          >
            {t("document.previewLoading", "Loading preview…")}
          </Text>
        }
        error={
          <Text id={`${idPrefix}-error`} py={4} color="red.500" fontSize="sm">
            {t("document.previewError", "Failed to load PDF preview.")}
          </Text>
        }
      >
        <Box
          id={`${idPrefix}-inner`}
          display="flex"
          flexDirection="row"
          gap={3}
          maxH={`${maxHeight}px`}
          minH={0}
          w="100%"
          overflow="hidden"
          alignItems="stretch"
        >
          {showThumbnails && (
            <Box
              id={`${idPrefix}-thumbnails`}
              flexShrink={0}
              w="100px"
              minW="100px"
              maxH={`${maxHeight}px`}
              overflowY="auto"
              overflowX="hidden"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
              py={1}
              borderRightWidth="1px"
              borderColor={cardBorder}
              pr={2}
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, i) => (
                  <Box
                    key={i}
                    id={`${idPrefix}-thumbnail-${i + 1}`}
                    onClick={() => {
                      setPdfPageNumber(i + 1);
                      pageRefsRef.current[i]?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                    cursor="pointer"
                    borderWidth="2px"
                    borderColor={
                      pdfPageNumber === i + 1 ? "blue.500" : "transparent"
                    }
                    borderRadius="md"
                    _hover={{ borderColor: "gray.400" }}
                  >
                    <Thumbnail
                      pageNumber={i + 1}
                      width={72}
                      onItemClick={({ pageNumber }) => {
                        setPdfPageNumber(pageNumber);
                        pageRefsRef.current[pageNumber - 1]?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                    />
                  </Box>
                ))}
            </Box>
          )}
          {!hideThumbnails && !isLargeScreen && !hideInlineThumbnailToggle && (
            <Box
              id={`${idPrefix}-thumbnail-toggle-wrap`}
              flexShrink={0}
              alignSelf="flex-start"
              pt={1}
            >
              <IconButton
                id={`${idPrefix}-thumbnail-toggle`}
                aria-label={
                  showThumbnails
                    ? t("document.hidePages", "Hide pages")
                    : t("document.showPages", "Show pages")
                }
                icon={
                  showThumbnails ? <ChevronLeftIcon /> : <ChevronRightIcon />
                }
                size="sm"
                variant="outline"
                onClick={() => setThumbnailExpanded((v) => !v)}
              />
            </Box>
          )}
          <Box
            id={`${idPrefix}-main`}
            ref={pageContainerRef}
            flex="1"
            minW={0}
            minWidth="200px"
            overflowY="auto"
            overflowX="hidden"
            display="flex"
            flexDirection="column"
            alignItems="center"
            position="relative"
          >
            {numPages != null &&
              Array.from({ length: numPages }, (_, i) => (
                <Box
                  key={i}
                  id={`${idPrefix}-page-${i + 1}`}
                  ref={(el) => {
                    pageRefsRef.current[i] = el;
                  }}
                >
                  <Page
                    pageNumber={i + 1}
                    width={pdfRenderWidth}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                </Box>
              ))}
          </Box>
        </Box>
      </Document>
    </Box>
  );
};

export default DocViewer;
